import { useCallback, useEffect, useState } from 'react';
import API_URL from '@/config/api';
import logger from '@/utils/logger';

/**
 * Fetch the training-settings singleton (fees, discount tiers, C&G values).
 * Cached in module scope so page navigations don't refetch — call `refresh()`
 * after a PUT to update the shared cache.
 */
let _cache = null;
let _cachePromise = null;

async function _load() {
  if (_cache) return _cache;
  if (_cachePromise) return _cachePromise;
  _cachePromise = fetch(`${API_URL}/api/staff/training-settings`, { credentials: 'include' })
    .then(async (r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      _cache = data;
      _cachePromise = null;
      return data;
    })
    .catch((e) => {
      _cachePromise = null;
      logger.warn('[useTrainingSettings] load failed, falling back to defaults:', e);
      return null;
    });
  return _cachePromise;
}

export function invalidateTrainingSettings() {
  _cache = null;
}

export default function useTrainingSettings() {
  const [settings, setSettings] = useState(_cache);
  const [loading, setLoading] = useState(!_cache);

  const reload = useCallback(async () => {
    _cache = null;
    setLoading(true);
    const s = await _load();
    setSettings(s);
    setLoading(false);
    return s;
  }, []);

  useEffect(() => {
    let mounted = true;
    if (_cache) {
      setSettings(_cache);
      setLoading(false);
      return () => { mounted = false; };
    }
    _load().then((s) => {
      if (mounted) {
        setSettings(s);
        setLoading(false);
      }
    });
    return () => { mounted = false; };
  }, []);

  return { settings, loading, reload };
}
